export enum QuoteEntityType {
  ORDER = 'Order',
  QUOTE = 'Quote',
}

export enum QuoteType {
  CUSTOMER = 'customer',
  VENDOR = 'vendor',
}

export enum QuoteVersionType {
  DRAFT = 'draft',
  RELEASE = 'release',
}

export enum QuoteStatus {
  DRAFT = 'draft',
  EDITABLE_BY_CUSTOMER = 'editable_by_customer',
  LOST = 'lost',
  OPEN = 'open',
  ORDER_ACCEPTED = 'accepted',
  ORDER_CANCELED = 'canceled',
  ORDER_DRAFT_CUSTOMER = 'draft-customer',
  ORDER_DRAFT_VENDOR = 'draft-vendor',
  ORDER_ORDER_FAILED = 'order-failed',
  ORDER_ORDERED = 'ordered',
  ORDER_ORDERING = 'ordering',
  ORDER_REQUESTED_BY_CUSTOMER = 'requested-by-customer',
  ORDER_SHIPPED_COMPLETE = 'shipped-complete',
  ORDER_SHIPPED_PARTIAL = 'shipped-partial',
  // The submit to the external order system never answered. Resolved only by the external
  // system reporting back (-> Ordered / Order Failed); nothing on our side clears it.
  ORDER_SUBMISSION_TIMED_OUT = 'submission-timed-out',
  PUNCHED_OUT = 'punched_out',
  WON = 'won',
}

export enum ReleaseType {
  EMAIL = 'email',
  NO_EMAIL = 'no_email',
}

export enum QuoteNoteType {
  CUSTOMER_NOTE = 'customer',
  INTERNAL_NOTE = 'internal',
}

export const QuoteStatusLabel: Record<QuoteStatus, string> = {
  [QuoteStatus.DRAFT]: 'Draft',
  [QuoteStatus.OPEN]: 'Open',
  [QuoteStatus.EDITABLE_BY_CUSTOMER]: 'Imported',
  [QuoteStatus.WON]: 'Ordered',
  [QuoteStatus.PUNCHED_OUT]: 'Punched Out',
  [QuoteStatus.LOST]: 'Lost',
  [QuoteStatus.ORDER_DRAFT_CUSTOMER]: 'Draft',
  [QuoteStatus.ORDER_DRAFT_VENDOR]: 'Draft',
  [QuoteStatus.ORDER_REQUESTED_BY_CUSTOMER]: 'Requested',
  [QuoteStatus.ORDER_ORDERED]: 'Ordered',
  [QuoteStatus.ORDER_ORDERING]: 'Ordering',
  [QuoteStatus.ORDER_ORDER_FAILED]: 'Order Failed',
  [QuoteStatus.ORDER_SUBMISSION_TIMED_OUT]: 'Submission Timed Out',
  [QuoteStatus.ORDER_ACCEPTED]: 'Accepted',
  [QuoteStatus.ORDER_CANCELED]: 'Canceled',
  [QuoteStatus.ORDER_SHIPPED_PARTIAL]: 'Shipped Partial',
  [QuoteStatus.ORDER_SHIPPED_COMPLETE]: 'Shipped Complete',
};

// SENDING is transient — the BE holds an in-flight claim; a later retry takes it over once stale.
export enum FilenetStatus {
  FAILED = 'failed',
  SENDING = 'sending',
  SENT = 'sent',
}

export const FilenetStatusLabel: Record<FilenetStatus, string> = {
  [FilenetStatus.FAILED]: 'Failed',
  [FilenetStatus.SENDING]: 'Sending',
  [FilenetStatus.SENT]: 'Sent',
};

export enum QuoteAction {
  ATTACH_EXISTING_VENDOR_QUOTE = 'attachExistingVendorQuote',
  CREATE_PROJECT = 'createProject',
}

export enum QuoteState {
  ACTIVE = 'Active',
  ARCHIVED = 'Archived',
}

// Send-as-SPA send lifecycle. Values are the exact BE casing (the api client camelCases keys, not
// values) and double as the chip label, so there is no label map. UPDATED_POST_SENT ("changed after
// it was sent") counts as sent for the Attributes lock; whether it can be resent is the BE's
// `canResendAsSpa` — use VendorQuoteHelper, not a bare === SENT. FAILED and PENDING are first
// sends.
export enum TradeAgreementStatus {
  FAILED = 'Failed',
  PENDING = 'Pending',
  SENT = 'Sent',
  UPDATED_POST_SENT = 'Updated Post Sent',
}

// Why a VQ's persisted Attributes make it unsendable as SPA. The check order lives in
// VendorQuoteHelper.getSpaSendBlockReason, mirroring AttributesService::assertExportable, so the
// reported reason matches the 422 the send would return. MISSING_REQUIRED_ATTRIBUTES stands for the
// whole required set: the BE refuses generically and the Attributes form marks the empty fields
// itself, so neither side names them.
export enum SpaSendBlockReason {
  ALL_CUSTOMERS_ALL_SALES_LOCATIONS_NOT_PERMITTED = 'allCustomersAllSalesLocationsNotPermitted',
  ALL_CUSTOMERS_NOT_PERMITTED = 'allCustomersNotPermitted',
  INVALID_TRADE_AGREEMENT_DATES = 'invalidTradeAgreementDates',
  MISSING_REQUIRED_ATTRIBUTES = 'missingRequiredAttributes',
  PROGRAM_NOT_PERMITTED = 'programNotPermitted',
}

// Sent by the BE on an alternate-version customer order whose project would make CPQ refuse the
// submit; when the project is both inactive and expired the BE reports INACTIVE.
export enum ProjectSubmitBlockReason {
  EXPIRED = 'expired',
  INACTIVE = 'inactive',
  NOT_STARTED = 'not_started',
}

// Sent instead of a block when the project's status lets the submit go through but CPQ users
// should know about it.
export enum ProjectSubmitWarningReason {
  CREDIT_HOLD = 'credit_hold',
}

export enum QuoteListAction {
  ADD_QUOTE = 'addQuote',
  CREATE_PROJECT = 'createProject',
  PROSPECT_QUOTE = 'prospectQuote',
}

export enum QuoteListType {
  COPY_PARTS_TO_QUOTE = 'copyPartsToQuote',
  GLOBAL = 'global',
  JOB = 'job',
  PROJECT = 'project',
}

export enum FreightStatus {
  COMBINED = 'combined',
  OMITTED = 'omitted',
  PREPAID_AND_CHARGE = 'prepaid_and_charge',
  SPREAD = 'spread',
}

export enum OrderType {
  BILL_AND_HOLD = 'bill_and_hold',
  BILLABLE = 'billable',
  HOLD_FOR_RELEASE = 'hold_for_release',
  SAMPLE_ORDER = 'sample_order',
}

export enum QuoteOrderPricingType {
  INCLUDE = 'include',
  OMIT = 'omit',
}

export enum QuotePartListTriggerAction {
  DOWNLOAD_PDF = 'download_pdf',
  DOWNLOAD_XLS = 'download_xls',
}

export enum UpsertQuoteAction {
  ARCHIVE = 'archive',
  CREATE = 'create',
  REASSIGN = 'reassign',
  RESTORE = 'restore',
  UPDATE = 'update',
}

export enum ExternalOrderStatusType {
  CANCELLED = 'Cancelled',
  CLOSED = 'Closed',
  DRAFT = 'Draft',
  OPEN = 'Open',
  SUBMITTED = 'Submitted',
}
